/**
 * KIOSK ATTRACT SCRIPT (B6): pure data + copy, spec §G (binding) and §F.
 *
 *   #  scene                  action                                  dwell        caption (kicker / line)
 *   0  home                   camera home, site tree rings, contours  8 s          LÁDERA CITY / Lohegaon, Pune. Beside the Indrayani.
 *   1  logistic-hubs          bloom → select nearest                  5 s + 6 s    AIRPORTS AND RAIL / {nearest}. {min} min by road, off-peak.
 *   2  business-parks         bloom                                   7 s          BUSINESS PARKS / {n} business parks. The nearest is {min} min by road, off-peak.
 *   3  education              bloom → select nearest                  5 s + 6 s    EDUCATION / {n} schools and colleges. The nearest is {min} min by road, off-peak.
 *   4  healthcare             bloom                                   7 s          HEALTHCARE / {nearest}. {min} min by road, off-peak.
 *   5  lifestyle-retail       bloom                                   7 s          LIFESTYLE AND RETAIL / {n} places to shop. The nearest is {min} min by road, off-peak.
 *   6  arterial-roads         bloom (corridors light)                 7 s          ARTERIAL ROADS / {n} arterial roads. The nearest access is {min} min by road, off-peak.
 *   7  future-infrastructure  planned corridors, camera fit           8 s          FUTURE INFRASTRUCTURE / Planned roads around the site. Alignments are indicative.
 *
 * Dwell counts from the scene's arrival (home settled, bloom:done, route:drawn), so one loop takes
 * about two minutes with the draws, exits and 600 ms pauses. Kickers are written in sentence case and
 * set in .ld-meta (tracked caps).
 */
import type { Place } from "@/contracts/data";
import { displayTravel } from "@/contracts/data";
import { fmtMin } from "@/features/card/format";
import type { Dataset } from "@/contracts/data";

export interface Caption {
  /** Stable per scene and loop, so a repeated caption still cross-fades. */
  key: string;
  kicker: string;
  line: string;
}

export interface CategoryStats {
  /** Places in the category (the site row is never counted). */
  n: number;
  /** Nearest by off-peak minutes (then by name), among places with a route. */
  nearest: Place | null;
  minutes: number | null;
}

export type SceneDef =
  | { index: number; kind: "home"; dwellMs: number; kicker: string; line: string }
  | {
      index: number;
      kind: "bloom";
      category: string;
      /** Hold after the bloom has arrived. */
      dwellMs: number;
      /** Then select the nearest place and hold this long after its route arrives. */
      selectDwellMs?: number;
      kicker: string;
      line: (s: CategoryStats) => string | null;
    };

/** §G timing (binding) and the waits around it. */
export const ATTRACT = {
  idleMs: 90_000,
  resetMs: 180_000,
  /** Pause between scenes, after the previous scene's exit. */
  pauseMs: 600,
  /** Longest wait for an exit to finish (0.7·Td for 140 km is 2380 ms; a bloom exit ≈ 2.4 s). */
  exitCapMs: 2800,
  /** Longest wait for a bloom to arrive (0.4·Tcam + (n−1)·s + dmax ≈ 3.8 s; slack for slow machines). */
  bloomCapMs: 9000,
  /** Longest wait for a selected route to arrive (Tarr ≤ 4360 ms). */
  selectCapMs: 8000,
  /** Longest wait for home() to settle. */
  homeCapMs: 6000,
  /** Every second loop runs at night (1100 drift at scene 0). */
  nightEvery: 2,
  /** Reload at a scene-0 boundary after this long, to free GPU memory (optional). */
  reloadAfterMs: 6 * 60 * 60 * 1000,
  /** Soft clear under a 640 drift cross-fade before scene 0. */
  softClearMs: 640,
} as const;

/** §F bloom nouns: [singular, plural]. */
const NOUNS: Record<string, [string, string]> = {
  "business-parks": ["business park", "business parks"],
  education: ["school or college", "schools and colleges"],
  "lifestyle-retail": ["place to shop", "places to shop"],
  "arterial-roads": ["arterial road", "arterial roads"],
  healthcare: ["hospital", "hospitals"],
  "logistic-hubs": ["airport or station", "airports and stations"],
};

/** "1 business park" · "24 business parks". */
export function countNoun(category: string, n: number): string {
  const [one, many] = NOUNS[category] ?? ["place", "places"];
  return `${n} ${n === 1 ? one : many}`;
}

/** §B.1 minutes ("48 min", "1 h 25 min") by road. */
const minText = (m: number) => `${fmtMin(m)} by road`;

export const ATTRACT_SCENES: readonly SceneDef[] = [
  { index: 0, kind: "home", dwellMs: 8000, kicker: "Ládera City", line: "Lohegaon, Pune. Beside the Indrayani." },
  {
    index: 1,
    kind: "bloom",
    category: "logistic-hubs",
    dwellMs: 5000,
    selectDwellMs: 6000,
    kicker: "Airports and rail",
    line: (s) => (s.nearest && s.minutes != null ? `${s.nearest.name}. ${minText(s.minutes)}, off-peak.` : null),
  },
  {
    index: 2,
    kind: "bloom",
    category: "business-parks",
    dwellMs: 7000,
    kicker: "Business parks",
    line: (s) => (s.minutes != null ? `${countNoun("business-parks", s.n)}. The nearest is ${minText(s.minutes)}, off-peak.` : null),
  },
  {
    index: 3,
    kind: "bloom",
    category: "education",
    dwellMs: 5000,
    selectDwellMs: 6000,
    kicker: "Education",
    line: (s) => (s.minutes != null ? `${countNoun("education", s.n)}. The nearest is ${minText(s.minutes)}, off-peak.` : null),
  },
  {
    index: 4,
    kind: "bloom",
    category: "healthcare",
    dwellMs: 7000,
    kicker: "Healthcare",
    line: (s) => (s.nearest && s.minutes != null ? `${s.nearest.name}. ${minText(s.minutes)}, off-peak.` : null),
  },
  {
    index: 5,
    kind: "bloom",
    category: "lifestyle-retail",
    dwellMs: 7000,
    kicker: "Lifestyle and retail",
    line: (s) => (s.minutes != null ? `${countNoun("lifestyle-retail", s.n)}. The nearest is ${minText(s.minutes)}, off-peak.` : null),
  },
  {
    index: 6,
    kind: "bloom",
    category: "arterial-roads",
    dwellMs: 7000,
    kicker: "Arterial roads",
    line: (s) => (s.minutes != null ? `${countNoun("arterial-roads", s.n)}. The nearest access is ${minText(s.minutes)}, off-peak.` : null),
  },
  {
    index: 7,
    kind: "bloom",
    category: "future-infrastructure",
    dwellMs: 8000,
    kicker: "Future infrastructure",
    line: () => "Planned roads around the site. Alignments are indicative.",
  },
];

/** Count and nearest place of a category (off-peak minutes, then name; the site row never counts). */
export function categoryStats(data: Pick<Dataset, "places"> | null, category: string): CategoryStats {
  if (!data) return { n: 0, nearest: null, minutes: null };
  const places = data.places.filter((p) => p.category === category && !p.isSite);
  let nearest: Place | null = null;
  let minutes: number | null = null;
  for (const p of places) {
    const t = displayTravel(p);
    if (!t) continue;
    if (minutes === null || t.minutes < minutes || (t.minutes === minutes && nearest && p.name.localeCompare(nearest.name) < 0)) {
      nearest = p;
      minutes = t.minutes;
    }
  }
  return { n: places.length, nearest, minutes };
}

/** The caption for a scene, or null when the data cannot fill it (the scene still runs, silently). */
export function captionFor(scene: SceneDef, data: Pick<Dataset, "places"> | null, loop = 0): Caption | null {
  const key = `${loop}:${scene.index}`;
  if (scene.kind === "home") return { key, kicker: scene.kicker, line: scene.line };
  const line = scene.line(categoryStats(data, scene.category));
  return line ? { key, kicker: scene.kicker, line } : null;
}

/** Mood for a loop. The time-of-day control is hidden and day is the only mood shown, so the
 *  attract loop stays in day too (loop is kept for the call signature). */
export function moodForLoop(_loop: number): "day" {
  return "day";
}
